import {
  useEffect,
  useMemo,
} from "react";

import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {
  Route,
  Routes,
  useBlocker,
} from "react-router-dom";
import {createSelector} from "reselect";

import {Footer} from "@/components/layouts/main/Footer";
import {Header} from "@/components/layouts/main/Header";
import {SideBar} from "@/components/layouts/main/SideBar";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {UnsavedPopup} from "@popups/attentionPopups/UnsavedPopup";
import {
  adminRoutes,
  createRoutes,
} from "@routers/lazyRoutes.jsx";

import styles from "./styles.module.scss";

const routes = createRoutes(adminRoutes);

const selectProductAndScenarioStates = createSelector(
  (state) => state.product?.create,
  (state) => state.product?.edit,
  (state) => state.scenario?.create,
  (state) => state.scenario?.edit,
  (state) => state.user?.edit,
  (state) => state.user?.create,
  (state) => state.video?.edit,
  (state) => state.video?.create,
  (state) => state.doctor?.create,
  (
    createProduct,
    editProduct,
    createScenario,
    editScenario,
    editUser,
    createUser,
    editVideo,
    createVideo,
    createDoctor,
  ) => ({
    createProduct: createProduct || {},
    editProduct: editProduct || {},
    createScenario: createScenario || {},
    editScenario: editScenario || {},
    editUser: editUser || {},
    createUser: createUser || {},
    editVideo,
    createVideo,
    createDoctor: createDoctor || {},
  }),
);

export const AdminPanel = () => {
  const {t} = useTranslation();
  const {openPopup, closePopup} = usePopup();

  const {
    createProduct: {isChanged: isProductCreate},
    editProduct: {isChanged: isProductEdit},
    createScenario: {isChanged: isScenarioCreate},
    editScenario: {isChanged: isScenarioEdit},
    editUser: {isChanged: isUserEdit},
    createUser: {isChanged: isUserCreate},
    editVideo: {isChanged: isVideoEdit},
    createVideo: {isChanged: isVideoCreate},
    createDoctor: {isChanged: isDoctorCreate},
  } = useSelector(selectProductAndScenarioStates);

  const blocker = useBlocker(({currentLocation, nextLocation}) => {
    const isUnsaved =
      isProductCreate ||
      isProductEdit ||
      isScenarioEdit ||
      isScenarioCreate ||
      isUserEdit ||
      isUserCreate ||
      isVideoEdit ||
      isVideoCreate ||
      isDoctorCreate;

    return isUnsaved && currentLocation.pathname !== nextLocation.pathname;
  });

  const {sessions, certifications, dashboard} = useSelector(
    (state) => state.common.viewTabs,
  );
  const isMayoly = useSelector((state) => state.common.settings.isMayoly);

  const adminBarList = useMemo(() => {
    const items = [
      {
        id: 0,
        iconId: "dashboard",
        name: t("sidebar.dashboard"),
        link:
          dashboard === "call"
            ? "dashboard/scenario"
            : "dashboard/certification",
      },
      {id: 1, iconId: "users", name: t("sidebar.users"), link: "users"},
      {
        id: 2,
        iconId: "scenarios",
        name: t("sidebar.scenario"),
        link: "call",
      },
      {id: 3, iconId: "doctors", name: t("sidebar.doctors"), link: "doctors"},
      {
        id: 4,
        iconId: "products",
        name: t("sidebar.product"),
        link: "products",
      },
      {id: 5, iconId: "library", name: t("sidebar.library"), link: "videos"},
      {
        id: 6,
        iconId: "analytics",
        name: t("sidebar.history"),
        link:
          sessions === "certification"
            ? "analytics/certification"
            : sessions === "scenario"
              ? "analytics/scenario"
              : "analytics/product",
      },
    ];

    return isMayoly ? items.filter((item) => item.link !== "doctors") : items;
  }, [t, sessions, certifications, dashboard, isMayoly]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      openPopup(
        <UnsavedPopup
          onClickYes={() => {
            blocker.proceed();
            closePopup();
          }}
          onClickNo={() => {
            blocker.reset();
            closePopup();
          }}
        />,
        {closeButton: false},
      );
    } else {
      closePopup();
    }
  }, [blocker]);

  return (
    <div className={styles.adminPanel}>
      <Header />

      <div
        style={{height: "auto"}}
        className={"container-big"}
      >
        <div className={styles.main}>
          <SideBar list={adminBarList} />

          <Routes>
            {routes &&
              routes.length > 0 &&
              routes.map((route, index) => (
                <Route
                  key={index}
                  path={route.path}
                  element={route.element}
                />
              ))}
          </Routes>
        </div>
      </div>

      <Footer />
    </div>
  );
};
